import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import Select from '@components/common/Select';
import Input from '@components/common/Input';
import CurrencyInput from '@components/common/CurrencyInput';
import Button from '@components/common/Button';
import { formatCurrency } from '@utils/formatters';

const transferSchema = z
  .object({
    fromAccountId: z.string().min(1, 'Selecione a conta de origem'),
    toAccountId: z.string().min(1, 'Selecione a conta de destino'),
    amount: z.coerce.number().positive('Valor deve ser maior que zero'),
    date: z.string().optional(),
    description: z.string().optional(),
  })
  .refine((d) => d.fromAccountId !== d.toAccountId, {
    message: 'Origem e destino devem ser diferentes',
    path: ['toAccountId'],
  });

const buildDefaults = (dv) => ({
  fromAccountId: dv?.fromAccountId || '',
  toAccountId: dv?.toAccountId || '',
  amount: '',
  date: new Date().toISOString().slice(0, 10),
  description: '',
});

const TransferForm = ({ accounts = [], defaultValues, onSubmit, onCancel, isLoading }) => {
  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(transferSchema),
    defaultValues: buildDefaults(defaultValues),
  });

  // Re-preenche quando abrir com outro pré-preenchimento (ex: "Pagar fatura" de um cartão).
  useEffect(() => {
    reset(buildDefaults(defaultValues));
  }, [defaultValues, reset]);

  const accountOptions = accounts.map((acc) => ({
    value: acc.accountId,
    label: `${acc.icon ? `${acc.icon} ` : ''}${acc.name} · ${formatCurrency(acc.currentBalance || 0)}`,
  }));

  const toAccount = accounts.find((a) => a.accountId === watch('toAccountId'));
  const isPayingCard = toAccount?.type === 'credit_card';

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <Select
        label="De (origem)"
        placeholder="Selecione a conta"
        options={accountOptions}
        error={errors.fromAccountId?.message}
        {...register('fromAccountId')}
      />

      <Select
        label="Para (destino)"
        placeholder="Selecione a conta"
        options={accountOptions}
        error={errors.toAccountId?.message}
        {...register('toAccountId')}
      />

      {isPayingCard && (
        <p className="-mt-2 text-xs text-primary-600 dark:text-primary-400">
          Pagamento de fatura — abate a dívida do cartão {toAccount.name}.
        </p>
      )}

      <Controller
        name="amount"
        control={control}
        render={({ field }) => (
          <CurrencyInput
            label="Valor"
            value={field.value}
            onValueChange={(values) => field.onChange(values.floatValue ?? '')}
            onBlur={field.onBlur}
            error={errors.amount?.message}
          />
        )}
      />

      <Input label="Data" type="date" error={errors.date?.message} {...register('date')} />

      <Input
        label="Descrição (opcional)"
        placeholder={isPayingCard ? 'Pagamento da fatura' : 'Ex: Transferência entre contas'}
        error={errors.description?.message}
        {...register('description')}
      />

      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancelar
        </Button>
        <Button type="submit" isLoading={isLoading}>
          {isPayingCard ? 'Pagar fatura' : 'Transferir'}
        </Button>
      </div>
    </form>
  );
};

export default TransferForm;
